import React, { useState, useEffect } from 'react'
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, Chip, Stack, IconButton, Tooltip, Textarea, CircularProgress } from '@mui/joy'
import { ClientCall, CallStatus } from '@numeo/types'
import { NumeoRestError } from '@numeo/types-core'
import axios, { isAxiosError } from 'axios'
import { AICallChat } from './AICallChat'
import { CallRecordPlayer } from 'components/shared/CallRecordPlayer'
import { HelpOutline, CheckCircle, Close, Check } from '@mui/icons-material'

interface AICallHistoryProps {
    callHistory: Array<{ callId: string }>
    activeCallId?: string
    fullHeight?: boolean
    reportedProblems?: Record<string, boolean>
    reportingProblems?: Record<string, boolean>
    onReportProblem?: (callId: string, message?: string) => void
}

export const AICallHistory: React.FC<AICallHistoryProps> = ({
    callHistory,
    activeCallId,
    fullHeight = false,
    reportedProblems = {},
    reportingProblems = {},
    onReportProblem,
}) => {
    const [expandedCalls, setExpandedCalls] = useState<string[]>([])
    const [callsModels, setCallsModels] = useState<ClientCall[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [dataFetched, setDataFetched] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [showBugReportInput, setShowBugReportInput] = useState<string | null>(null)
    const [bugReportMessage, setBugReportMessage] = useState('')

    // Get call IDs that need to be fetched from API
    const callIds = callHistory.map(({ callId }) => callId)

    const fetchCallData = async (): Promise<void> => {
        if (callIds.length === 0) return

        try {
            setIsLoading(true)
            setErrorMessage('')
            const response = await axios.post('trucks/load/calls', {
                callIds,
            })
            setCallsModels(response.data.data)
            if (response.data.data[response.data.data.length - 1].callId) {
                setExpandedCalls([response.data.data[response.data.data.length - 1].callId])
            }
            setDataFetched(true)
        } catch (error) {
            console.error('Error fetching call data:', error)
            if (isAxiosError(error)) {
                const numeoError = { message: error.message } as NumeoRestError
                setErrorMessage(numeoError.message)
            } else {
                setErrorMessage('Failed to load call history')
            }
        } finally {
            setIsLoading(false)
        }
    }

    // Fetch call data on mount or when call history changes
    useEffect(() => {
        if (callHistory.length > 0 && !dataFetched) {
            fetchCallData()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [callHistory.length, dataFetched])

    const formatDuration = (startedAt: number, endedAt?: number) => {
        if (!endedAt) return 'In Progress'
        const duration = Math.floor((endedAt - startedAt) / 1000)
        const minutes = Math.floor(duration / 60)
        const seconds = duration % 60
        return `${minutes}:${seconds.toString().padStart(2, '0')}`
    }

    const getStatusColor = (status: CallStatus) => {
        switch (status) {
            case CallStatus.CONNECTED:
                return 'success'
            case CallStatus.ENDED:
                return 'neutral'
            case CallStatus.ERROR:
                return 'danger'
            default:
                return 'warning'
        }
    }

    const getCallDisplayData = () => {
        // Merge data from callsModels and live calls map
        const allCalls = callHistory
            .map(({ callId }, index) => {
                // Then try from fetched models
                const modelCall = callsModels.find((c) => c.callId === callId)
                if (modelCall) {
                    return { call: modelCall, index, callId }
                }

                return null
            })
            .filter(Boolean) as Array<{ call: ClientCall; index: number; callId: string }>

        return allCalls.reverse() // Show most recent first
    }

    const handleReportProblem = (e: React.MouseEvent, callId: string) => {
        e.stopPropagation()
        if (showBugReportInput === callId) {
            handleBugReportCancel()
        } else {
            setShowBugReportInput(callId)
        }
    }

    const handleBugReportSubmit = (callId: string) => {
        if (bugReportMessage.trim() && onReportProblem) {
            onReportProblem(callId, bugReportMessage)
            handleBugReportCancel()
        }
    }

    const handleBugReportCancel = () => {
        setShowBugReportInput(null)
        setBugReportMessage('')
    }

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                <CircularProgress size="sm" />
                <Typography level="body-sm" color="neutral">
                    Loading call history...
                </Typography>
            </Box>
        )
    }

    if (errorMessage) {
        return (
            <Box>
                <Typography level="title-sm" sx={{ mb: 2 }}>
                    ClientCall History ({callHistory.length})
                </Typography>
                <Typography level="body-sm" color="danger">
                    Error: {errorMessage}
                </Typography>
            </Box>
        )
    }

    const displayCalls = getCallDisplayData()

    return (
        <Box>
            <Typography level="title-sm" sx={{ mb: 2 }}>
                ClientCall History ({callHistory.length})
            </Typography>

            {displayCalls.length === 0 ? (
                <Typography level="body-sm" color="neutral">
                    No call history available
                </Typography>
            ) : (
                <Box
                    sx={{
                        height: fullHeight ? '100%' : '400px',
                        overflow: 'auto',
                        pr: 1,
                        '&::-webkit-scrollbar': {
                            width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: 'neutral.100',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'neutral.300',
                            borderRadius: '4px',
                            '&:hover': {
                                backgroundColor: 'neutral.400',
                            },
                        },
                    }}
                >
                    <Stack spacing={1}>
                        {displayCalls.map(({ call, index, callId }) => {
                            const isExpanded = expandedCalls.includes(callId)
                            const isActiveCall = activeCallId === callId
                            const callNumber = callHistory.length - index
                            const isReported = reportedProblems[callId]
                            const isReporting = reportingProblems[callId] || false

                            return (
                                <Accordion
                                    key={callId}
                                    expanded={isExpanded}
                                    onChange={(_, expanded) => {
                                        setExpandedCalls((prev) => (expanded ? [...prev, callId] : prev.filter((id) => id !== callId)))
                                    }}
                                    sx={{
                                        boxShadow: 'sm',
                                        '&:hover': {
                                            boxShadow: 'md',
                                        },
                                        transition: 'box-shadow 0.2s ease-in-out',
                                        // Highlight active call
                                        ...(isActiveCall && {
                                            border: '2px solid',
                                            borderColor: 'primary.500',
                                        }),
                                    }}
                                >
                                    <AccordionSummary
                                        sx={{
                                            backgroundColor: isActiveCall ? 'primary.50' : 'background.level1',
                                            borderRadius: 'lg',
                                            my: 1,
                                            px: 1,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                {/* ClientCall status indicator */}
                                                <Box
                                                    sx={{
                                                        width: 10,
                                                        height: 10,
                                                        borderRadius: '50%',
                                                        bgcolor:
                                                            call.status === CallStatus.CONNECTED
                                                                ? 'success.500'
                                                                : call.status === CallStatus.ENDED
                                                                  ? 'neutral.500'
                                                                  : call.status === CallStatus.ERROR
                                                                    ? 'danger.500'
                                                                    : 'warning.500',
                                                        flexShrink: 0,
                                                        // Animate active calls
                                                        ...(isActiveCall &&
                                                            [CallStatus.CONNECTED, CallStatus.INITIATING].includes(call.status) && {
                                                                animation: 'pulse 2s infinite',
                                                                '@keyframes pulse': {
                                                                    '0%': { opacity: 1 },
                                                                    '50%': { opacity: 0.5 },
                                                                    '100%': { opacity: 1 },
                                                                },
                                                            }),
                                                    }}
                                                />
                                                <Typography level="body-sm" sx={{ fontWeight: isActiveCall ? 'bold' : 'normal' }}>
                                                    ClientCall #{callNumber} {isActiveCall && '(Current)'}
                                                </Typography>
                                                <Typography level="body-xs" color="neutral">
                                                    {call.startedAt ? new Date(call.startedAt).toLocaleString() : 'Unknown time'}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography level="body-xs" color="neutral">
                                                    {formatDuration(call.startedAt || 0, call.endedAt)}
                                                </Typography>
                                                <Chip size="sm" color={getStatusColor(call.status)} variant="soft">
                                                    {call.status === CallStatus.ENDED ? 'Completed' : call.status}
                                                </Chip>
                                                {onReportProblem && (
                                                    <Box sx={{ position: 'relative' }}>
                                                        <Tooltip title={isReported ? "Issue reported. We'll take a look!" : 'Report an issue with this call'}>
                                                            <IconButton
                                                                size="sm"
                                                                variant="plain"
                                                                color={isReported ? 'success' : 'danger'}
                                                                onClick={(e) => handleReportProblem(e, callId)}
                                                                disabled={isReporting}
                                                            >
                                                                {isReporting ? (
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            height: 20,
                                                                            width: 20,
                                                                        }}
                                                                    >
                                                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                                            <circle cx="12" cy="12" r="10" stroke="#1976d2" strokeWidth="2" opacity="0.2" />
                                                                            <circle
                                                                                cx="12"
                                                                                cy="12"
                                                                                r="8"
                                                                                stroke="#1976d2"
                                                                                strokeWidth="2"
                                                                                strokeDasharray="25"
                                                                                strokeDashoffset="10"
                                                                            />
                                                                        </svg>
                                                                    </Box>
                                                                ) : isReported ? (
                                                                    <CheckCircle fontSize="small" />
                                                                ) : (
                                                                    <HelpOutline fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                        </Tooltip>
                                                        {showBugReportInput === callId && (
                                                            <Box
                                                                sx={{
                                                                    position: 'absolute',
                                                                    top: '100%',
                                                                    right: 0,
                                                                    mt: 0.5,
                                                                    p: 1,
                                                                    bgcolor: 'background.popup',
                                                                    border: '1px solid',
                                                                    borderColor: 'divider',
                                                                    borderRadius: 'sm',
                                                                    boxShadow: 'md',
                                                                    zIndex: 1000,
                                                                }}
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', width: 300 }}>
                                                                    <Textarea
                                                                        placeholder="Describe the issue..."
                                                                        value={bugReportMessage}
                                                                        onChange={(e) => setBugReportMessage(e.target.value)}
                                                                        sx={{ flex: 1 }}
                                                                        size="sm"
                                                                        minRows={1}
                                                                        maxRows={3}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                                                                e.preventDefault()
                                                                                handleBugReportSubmit(callId)
                                                                            }
                                                                            if (e.key === 'Escape') {
                                                                                handleBugReportCancel()
                                                                            }
                                                                        }}
                                                                        autoFocus
                                                                    />
                                                                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5 }}>
                                                                        <IconButton
                                                                            size="sm"
                                                                            variant="solid"
                                                                            color="primary"
                                                                            onClick={() => handleBugReportSubmit(callId)}
                                                                            disabled={!bugReportMessage.trim() || isReporting}
                                                                            title="Send report"
                                                                        >
                                                                            <Check fontSize="small" />
                                                                        </IconButton>
                                                                        <IconButton
                                                                            size="sm"
                                                                            variant="outlined"
                                                                            color="neutral"
                                                                            onClick={handleBugReportCancel}
                                                                            disabled={isReporting}
                                                                            title="Cancel"
                                                                        >
                                                                            <Close fontSize="small" />
                                                                        </IconButton>
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    </AccordionSummary>

                                    <AccordionDetails sx={{ bgcolor: 'background.surface' }}>
                                        {/* ClientCall Recording - Only load when accordion is expanded */}
                                        {call.recordURL && <CallRecordPlayer recordURL={call.recordURL} />}

                                        {/* Transcript Display */}
                                        <AICallChat call={call} fullHeight={fullHeight} />
                                    </AccordionDetails>
                                </Accordion>
                            )
                        })}
                    </Stack>
                </Box>
            )}
        </Box>
    )
}
